import React, { useEffect, useState, useCallback } from 'react';
import { API_BASE_URL } from '../../../config';
import { publishSidebar } from '../../../stores/chartSidebarStore';

function Avatar({ url, name, size = 7 }) {
  const [err, setErr] = useState(false);
  const sz = `w-${size} h-${size}`;
  if (url && !err) {
    return (
      <img
        src={url}
        alt={name}
        onError={() => setErr(true)}
        className={`${sz} rounded-full object-cover flex-shrink-0`}
      />
    );
  }
  return (
    <div className={`${sz} rounded-full bg-gray-600 flex items-center justify-center flex-shrink-0 text-xs font-semibold text-gray-200`}>
      {name?.[0]?.toUpperCase() ?? '?'}
    </div>
  );
}

export default function AnalystFilterTab({ currentSymbol, state, onChange }) {
  const [analysts, setAnalysts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const fetchAnalysts = useCallback(async () => {
    if (!currentSymbol || !currentSymbol.includes(':')) return;
    const [exchange, ticker] = currentSymbol.split(':');
    setLoading(true);
    // plan 181 fix (#6): clear the published available-list NOW so an external
    // reader (CLI getSidebar / `brochart analysts list`) sees empty/loading during
    // the async fetch for the new symbol — never the PREVIOUS symbol's analysts,
    // which would be misattributed to the current symbol.
    publishSidebar({ analysts: { available: [] } });
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(
        `${API_BASE_URL}/api/research/symbols/${exchange}/${ticker}/predictions/?page_size=200`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      const results = data.results ?? data ?? [];
      const seen = new Set();
      const unique = [];
      for (const p of results) {
        if (!seen.has(p.analyst_slug)) {
          seen.add(p.analyst_slug);
          unique.push({
            slug: p.analyst_slug,
            name: p.analyst_name,
            imageUrl: p.analyst_image_url,
            followers: p.analyst_twitter_followers,
          });
        }
      }
      setAnalysts(unique);
      // plan 181: publish the available-analysts list so the CLI (window.__brochart
      // getSidebar) can see what's loadable for this symbol.
      publishSidebar({ analysts: { available: unique.map(a => ({ slug: a.slug, name: a.name, followers: a.followers })) } });
    } catch {
      setAnalysts([]);
      publishSidebar({ analysts: { available: [] } });
    } finally {
      setLoading(false);
    }
  }, [currentSymbol]);

  useEffect(() => { fetchAnalysts(); }, [fetchAnalysts]);

  const toggleAnalyst = (slug) => {
    const current = state.selectedAnalysts ?? [];
    const next = current.includes(slug)
      ? current.filter(s => s !== slug)
      : [...current, slug];
    onChange({ ...state, selectedAnalysts: next });
  };

  const clearAll = () => onChange({ ...state, selectedAnalysts: [] });

  const filtered = analysts.filter(a =>
    !search || a.name?.toLowerCase().includes(search.toLowerCase())
  );

  const selected = state.selectedAnalysts ?? [];

  return (
    <div className="flex flex-col h-full">
      <div className="p-3 space-y-2 border-b border-gray-700">
        <input
          type="text"
          placeholder="Search analysts..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full bg-gray-700 text-gray-200 placeholder-gray-500 text-sm rounded-md px-3 py-1.5 border border-gray-600 focus:outline-none focus:border-blue-500"
        />
        <div className="flex items-center justify-between text-xs text-gray-400">
          <span>{selected.length > 0 ? `${selected.length} selected` : 'All analysts'}</span>
          {selected.length > 0 && (
            <button onClick={clearAll} className="text-blue-400 hover:text-blue-300">
              Clear all
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {!loading && !currentSymbol?.includes(':') && (
          <p className="text-xs text-gray-500 text-center py-8 px-4">
            Load a symbol in the chart to see analysts
          </p>
        )}
        {!loading && filtered.length === 0 && currentSymbol?.includes(':') && (
          <p className="text-xs text-gray-500 text-center py-8 px-4">
            No analysts found for this symbol
          </p>
        )}
        {!loading && filtered.map(analyst => {
          const isSelected = selected.includes(analyst.slug);
          return (
            <button
              key={analyst.slug}
              onClick={() => toggleAnalyst(analyst.slug)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 text-left border-b border-gray-700/50 transition-colors ${
                isSelected ? 'bg-blue-900/30' : 'hover:bg-gray-700/40'
              }`}
            >
              <div className={`w-4 h-4 rounded flex-shrink-0 border flex items-center justify-center transition-colors ${
                isSelected ? 'bg-blue-500 border-blue-500' : 'border-gray-500'
              }`}>
                {isSelected && (
                  <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
              <Avatar url={analyst.imageUrl} name={analyst.name} size={7} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-gray-200 truncate">{analyst.name}</div>
                {analyst.followers && (
                  <div className="text-xs text-gray-500">
                    {(analyst.followers / 1000).toFixed(0)}K followers
                  </div>
                )}
              </div>
            </button>
          );
        })}
      </div>

      {selected.length > 0 && (
        <div className="p-3 border-t border-gray-700">
          <div className="text-xs text-gray-400 mb-1.5">Direction filter</div>
          <div className="flex rounded-md overflow-hidden border border-gray-600 text-xs">
            {['all', 'bullish', 'bearish'].map(d => (
              <button
                key={d}
                onClick={() => onChange({ ...state, direction: d })}
                className={`flex-1 py-1.5 capitalize transition-colors ${
                  state.direction === d
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
